import { useCallback, useState } from 'react';

import useGetPDFPreviews from 'approot/shared/pdf-preview/pdf-preview.hooks';
import type { GetPdfPreviewsQuery } from '../../../shared/pdf-preview/pdf-preview.graphql.generated';

const useDocumentPreview = ({
  documentKey,
  previewSize,
  previewWidth,
  previewHeight,
  onCompleted,
}: {
  documentKey: string;
  previewSize: number;
  previewWidth?: number;
  previewHeight?: number;
  onCompleted?: (data: GetPdfPreviewsQuery) => void;
}) => {
  const [currentImage, setCurrentImage] = useState<string>();

  const { data } = useGetPDFPreviews({
    documentKey,
    previewSize,
    previewWidth,
    previewHeight,
    onCompleted: onCompleted
      ? onCompleted
      : res => {
          const preview = res.getPDFPreviews?.[0]?.urls?.[0]?.url;
          setCurrentImage(preview || undefined);
        },
  });

  const pagePreviews = data?.getPDFPreviews;

  const getPagePreviewIndex = useCallback(() => {
    return (
      pagePreviews?.findIndex(page => page?.urls[0].url === currentImage) || 0
    );
  }, [pagePreviews, currentImage]);

  const getPrevPreviewUrl = useCallback(() => {
    const currentIndex = getPagePreviewIndex();
    return currentIndex !== undefined
      ? pagePreviews?.[currentIndex - 1]?.urls[0].url
      : undefined;
  }, [getPagePreviewIndex, pagePreviews]);

  const getNextPreviewUrl = useCallback(() => {
    const currentIndex = getPagePreviewIndex();
    return currentIndex !== undefined
      ? pagePreviews?.[currentIndex + 1]?.urls[0].url
      : undefined;
  }, [getPagePreviewIndex, pagePreviews]);

  const getPrevPreviewImage = useCallback(() => {
    const prevUrl = getPrevPreviewUrl();
    if (prevUrl) {
      setCurrentImage(prevUrl);
    }
  }, [getPrevPreviewUrl]);

  const getNextPreviewImage = useCallback(() => {
    const nextUrl = getNextPreviewUrl();
    if (nextUrl) {
      setCurrentImage(nextUrl);
    }
  }, [getNextPreviewUrl]);

  const previewPageCount = Number(pagePreviews?.length || 1);

  return {
    currentImage,
    setCurrentImage,
    getPagePreviewIndex,
    getPrevPreviewImage,
    getNextPreviewImage,
    previewPageCount,
    pagePreviews,
  };
};

export default useDocumentPreview;
